import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "./auth";
import {
  clearExtensionAuthToken,
  pingExtension,
  pushAuthTokenToExtension,
  type IExtPingResponse,
} from "../extension";

// Firebase ID tokens are valid for 1h. Refresh well before expiry to keep
// long-running extension sessions authenticated.
const REFRESH_INTERVAL_MS = 30 * 60 * 1000;

interface IExtensionBridgeState {
  /** True once the extension has acked at least one ping. */
  isInstalled: boolean;
  /** Installed extension version, if known. */
  version: string | null;
  /** True after the most recent successful token push. */
  isAuthorized: boolean;
}

const initialState: IExtensionBridgeState = {
  isInstalled: false,
  version: null,
  isAuthorized: false,
};

const ExtensionBridgeContext =
  createContext<IExtensionBridgeState>(initialState);

export const useExtensionBridge = () => useContext(ExtensionBridgeContext);

export const ExtensionBridgeProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { isAuthenticated, isLoading, user, getToken } = useAuth();
  const extensionId = process.env.REACT_APP_EXTENSION_ID;

  const [state, setState] = useState<IExtensionBridgeState>(initialState);
  // Latest values inside intervals / event handlers — avoids stale closures
  // without re-creating the interval every auth change.
  const isAuthenticatedRef = useRef(isAuthenticated);
  const userRef = useRef(user);
  const getTokenRef = useRef(getToken);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
    userRef.current = user;
    getTokenRef.current = getToken;
  }, [isAuthenticated, user, getToken]);

  const pushFreshToken = useCallback(async () => {
    if (!extensionId) return;
    if (!isAuthenticatedRef.current) return;
    const token = await getTokenRef.current();
    if (!token) return;
    const currentUser = userRef.current;
    const ack = await pushAuthTokenToExtension(
      extensionId,
      token,
      currentUser
        ? {
            uid: currentUser._id,
            email: currentUser.email,
            role: currentUser.role,
          }
        : undefined
    );
    if (ack?.ok) {
      setState((s) => ({ ...s, isAuthorized: true }));
    }
  }, [extensionId]);

  // 1. Detect the extension once on mount.
  useEffect(() => {
    if (!extensionId) return;
    let cancelled = false;
    pingExtension(extensionId).then((res: IExtPingResponse | null) => {
      if (cancelled || !res?.installed) return;
      setState((s) => ({
        ...s,
        isInstalled: true,
        version: res.version ?? null,
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [extensionId]);

  // 2. Push / clear the token whenever the auth state flips.
  // Guard on isLoading: Firebase auth state is async — isAuthenticated starts
  // as false before the listener fires. Without this guard, every new tab
  // immediately sends clearAuthToken, wiping the token mid-session in other
  // tabs that are already running extension automation.
  useEffect(() => {
    if (!extensionId) return;
    if (isLoading) return;
    if (isAuthenticated) {
      pushFreshToken();
    } else {
      clearExtensionAuthToken(extensionId).then(() => {
        setState((s) => ({ ...s, isAuthorized: false }));
      });
    }
  }, [extensionId, isAuthenticated, isLoading, pushFreshToken]);

  // 3. Periodic refresh + on tab focus.
  useEffect(() => {
    if (!extensionId) return;

    const interval = window.setInterval(
      () => void pushFreshToken(),
      REFRESH_INTERVAL_MS
    );

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void pushFreshToken();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [extensionId, pushFreshToken]);

  return (
    <ExtensionBridgeContext.Provider value={state}>
      {children}
    </ExtensionBridgeContext.Provider>
  );
};
